import { useEffect, useState } from "react";
import {
  CheckCircle2,
  FileCheck2,
  Loader2,
  PenLine,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { useRoute } from "wouter";
import SignaturePad from "@/components/SignaturePad";
import SignatureUpload from "@/components/SignatureUpload";

type AgreementView = {
  requestId: string;
  state: string;
  expiresAt: string;
  configuration: any;
  recipient: { printedName: string };
  attachments: Array<{
    requirementId: string;
    artifactId: string;
    contentType: string;
    bytes: number;
  }>;
};

export default function AgreementSigning() {
  const [, params] = useRoute("/agreements/sign/:requestId");
  const requestId = params?.requestId ?? "";
  const [session, setSession] = useState("");
  const [agreement, setAgreement] = useState<AgreementView | null>(null);
  const [documentUrl, setDocumentUrl] = useState("");
  const [printedName, setPrintedName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [mode, setMode] = useState<"drawn" | "uploaded">("drawn");
  const [consent, setConsent] = useState(false);
  const [fields, setFields] = useState<Record<string, string | boolean>>({});
  const [uploadedAttachments, setUploadedAttachments] = useState<
    Record<string, { contentType: string; bytes: number }>
  >({});
  const [uploadingAttachment, setUploadingAttachment] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  const [complete, setComplete] = useState(false);
  const [completionState, setCompletionState] = useState("");

  useEffect(() => {
    let objectUrl = "";
    const start = async () => {
      try {
        let auth = sessionStorage.getItem(`agreement:${requestId}`) ?? "";
        const fragment = window.location.hash.slice(1);
        if (fragment) {
          const dot = fragment.indexOf(".");
          if (dot < 1) throw new Error("This signing link is incomplete.");
          const response = await fetch("/api/agreements/signing/exchange", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              requestId,
              credentialId: fragment.slice(0, dot),
              token: fragment.slice(dot + 1),
            }),
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.error);
          auth = `${result.id}.${result.rawToken}`;
          sessionStorage.setItem(`agreement:${requestId}`, auth);
          history.replaceState(null, "", window.location.pathname);
        }
        if (!auth)
          throw new Error("Open the secure signing link you received.");
        setSession(auth);
        const headers = { Authorization: `AgreementSession ${auth}` };
        const [detailResponse, pdfResponse] = await Promise.all([
          fetch(`/api/agreements/signing/${requestId}`, { headers }),
          fetch(`/api/agreements/signing/${requestId}/document`, { headers }),
        ]);
        const detail = await detailResponse.json();
        if (!detailResponse.ok) throw new Error(detail.error);
        if (!pdfResponse.ok) throw new Error((await pdfResponse.json()).error);
        setAgreement(detail);
        setUploadedAttachments(
          Object.fromEntries(
            (detail.attachments ?? []).map((item: any) => [
              item.requirementId,
              { contentType: item.contentType, bytes: item.bytes },
            ])
          )
        );
        setPrintedName(detail.recipient.printedName);
        objectUrl = URL.createObjectURL(await pdfResponse.blob());
        setDocumentUrl(objectUrl);
      } catch (cause) {
        setError(
          cause instanceof Error
            ? cause.message
            : "The agreement could not be opened."
        );
      } finally {
        setBusy(false);
      }
    };
    void start();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [requestId]);

  const uploadAttachment = async (requirementId: string, file?: File) => {
    if (!file) return;
    setUploadingAttachment(requirementId);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await fetch(
        `/api/agreements/signing/${requestId}/attachments/${requirementId}`,
        {
          method: "POST",
          headers: { Authorization: `AgreementSession ${session}` },
          body,
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setUploadedAttachments(current => ({
        ...current,
        [requirementId]: {
          contentType: result.contentType,
          bytes: result.bytes,
        },
      }));
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The supporting document could not be uploaded."
      );
    } finally {
      setUploadingAttachment("");
    }
  };

  const submit = async () => {
    if (!signature || !consent || printedName.trim().length < 2)
      return setError("Complete your name, consent, and signature.");
    setBusy(true);
    setError("");
    try {
      const response = await fetch(
        `/api/agreements/signing/${requestId}/submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `AgreementSession ${session}`,
          },
          body: JSON.stringify({
            printedName,
            consentAccepted: true,
            fields,
            idempotencyKey: crypto.randomUUID(),
            signaturePngBase64: signature.split(",")[1] ?? "",
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setCompletionState(result.state ?? "");
      setComplete(true);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The agreement could not be submitted."
      );
    } finally {
      setBusy(false);
    }
  };

  if (busy && !agreement)
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Opening secure agreement...
        </div>
      </main>
    );
  if (error && !agreement)
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-bold text-slate-900">
            Agreement unavailable
          </h1>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
        </div>
      </main>
    );
  if (complete)
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-[#84ab4a]" />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">
            Signature received
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {completionState === "awaiting_company"
              ? "Your signed agreement was sent to the administrator for acknowledgment. You will receive the completed copy after it is finalized."
              : "Your agreement is being finalized and stored with your record."}
          </p>
        </div>
      </main>
    );

  const customFields =
    agreement?.configuration.fields.filter(
      (field: any) =>
        field.role === "recipient" &&
        ["text", "checkbox", "selection"].includes(field.kind)
    ) ?? [];
  const attachmentRequirements = agreement?.configuration.attachments ?? [];
  const missingRequiredAttachment = attachmentRequirements.some(
    (requirement: any) =>
      requirement.required && !uploadedAttachments[requirement.id]
  );
  return (
    <main className="min-h-screen bg-slate-100 text-slate-900">
      <header className="border-b border-slate-200 bg-white px-5 py-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#6f923d]">
              BizConnect360
            </div>
            <h1 className="mt-1 text-lg font-bold">
              {agreement?.configuration.title}
            </h1>
          </div>
          <span className="text-xs text-slate-500">
            Expires {new Date(agreement!.expiresAt).toLocaleDateString()}
          </span>
        </div>
      </header>
      <div className="mx-auto grid max-w-7xl gap-6 p-4 lg:grid-cols-[minmax(0,1fr)_390px] lg:p-6">
        <section className="min-h-[70vh] overflow-hidden rounded-xl border border-slate-200 bg-white">
          {documentUrl ? (
            <>
              <iframe
                title="Agreement document"
                src={documentUrl}
                className="h-[75vh] w-full"
              />
              <a
                href={documentUrl}
                target="_blank"
                rel="noreferrer"
                className="block border-t border-slate-100 px-4 py-3 text-center text-xs font-semibold text-[#6f923d] lg:hidden"
              >
                Open document in a new tab
              </a>
            </>
          ) : (
            <div className="grid h-[75vh] place-items-center text-sm text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="sr-only">Loading agreement document</span>
            </div>
          )}
        </section>
        <aside className="space-y-5 lg:sticky lg:top-6 lg:self-start">
          <div>
            <h2 className="text-base font-bold">Review and sign</h2>
            <p className="mt-1 text-sm text-slate-500">
              Read the full document before completing these fields.
            </p>
          </div>
          <label className="block text-sm font-semibold">
            Full legal name
            <input
              value={printedName}
              onChange={event => setPrintedName(event.target.value)}
              className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal outline-none focus:border-[#84ab4a]"
            />
          </label>
          {customFields.map((field: any) =>
            field.kind === "checkbox" ? (
              <label key={field.id} className="flex gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={fields[field.id] === true}
                  onChange={event =>
                    setFields({ ...fields, [field.id]: event.target.checked })
                  }
                />
                <span>{field.label}</span>
              </label>
            ) : field.kind === "selection" ? (
              <label key={field.id} className="block text-sm font-semibold">
                {field.label}
                <select
                  value={String(fields[field.id] ?? "")}
                  onChange={event =>
                    setFields({ ...fields, [field.id]: event.target.value })
                  }
                  className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5 font-normal"
                >
                  <option value="">Select</option>
                  {field.options.map((option: string) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              </label>
            ) : (
              <label key={field.id} className="block text-sm font-semibold">
                {field.label}
                <input
                  value={String(fields[field.id] ?? "")}
                  onChange={event =>
                    setFields({ ...fields, [field.id]: event.target.value })
                  }
                  className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5 font-normal"
                />
              </label>
            )
          )}
          {attachmentRequirements.length > 0 && (
            <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#6f923d]" />
                <div>
                  <h3 className="text-sm font-bold">Supporting documents</h3>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Files are encrypted in private storage and connected to this
                    agreement request. Each required item must be uploaded
                    before submission.
                  </p>
                </div>
              </div>
              {attachmentRequirements.map((requirement: any) => {
                const uploaded = uploadedAttachments[requirement.id];
                const uploading = uploadingAttachment === requirement.id;
                return (
                  <label
                    key={requirement.id}
                    className="block cursor-pointer rounded-lg border border-slate-200 p-3 hover:border-[#84ab4a]"
                  >
                    <span className="flex items-center justify-between gap-3 text-sm font-semibold">
                      <span>
                        {requirement.label}
                        {requirement.required ? " *" : ""}
                      </span>
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin text-[#6f923d]" />
                      ) : uploaded ? (
                        <FileCheck2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <Upload className="h-4 w-4 text-slate-400" />
                      )}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500">
                      {requirement.description}
                    </span>
                    <span className="mt-1 block text-[11px] text-slate-400">
                      {uploaded
                        ? `Uploaded (${Math.ceil(uploaded.bytes / 1024)} KB). Choose another file to replace it.`
                        : `PDF, PNG, or JPEG. Maximum ${Math.floor(requirement.maxBytes / 1024 / 1024)} MB.`}
                    </span>
                    <input
                      type="file"
                      className="sr-only"
                      disabled={Boolean(uploadingAttachment)}
                      accept={requirement.allowedContentTypes.join(",")}
                      onChange={event =>
                        void uploadAttachment(
                          requirement.id,
                          event.target.files?.[0]
                        )
                      }
                    />
                  </label>
                );
              })}
            </section>
          )}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold">Signature</span>
              <div className="flex rounded-lg bg-slate-200 p-0.5">
                {(
                  [
                    ["drawn", "Draw", PenLine],
                    ["uploaded", "Upload", Upload],
                  ] as const
                ).map(([key, label, Icon]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setMode(key);
                      setSignature(null);
                    }}
                    className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs ${mode === key ? "bg-white shadow-sm" : "text-slate-500"}`}
                  >
                    <Icon className="h-3 w-3" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {mode === "drawn" ? (
              <SignaturePad theme="light" onChange={setSignature} />
            ) : (
              <SignatureUpload
                theme="light"
                value={signature}
                onChange={setSignature}
              />
            )}
          </div>
          <label className="flex gap-3 rounded-xl bg-white p-3 text-xs leading-5 text-slate-600">
            <input
              type="checkbox"
              className="mt-1"
              checked={consent}
              onChange={event => setConsent(event.target.checked)}
            />
            <span>{agreement?.configuration.disclosureText}</span>
          </label>
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <button
            disabled={
              busy ||
              Boolean(uploadingAttachment) ||
              !signature ||
              !consent ||
              missingRequiredAttachment
            }
            onClick={submit}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#84ab4a] px-4 py-3 font-semibold text-white hover:bg-[#73963f] disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Submit
            signed agreement
          </button>
          <p className="text-center text-[11px] text-slate-400">
            The signing date is applied by the server when your submission is
            accepted.
          </p>
        </aside>
      </div>
    </main>
  );
}
